import type { Element, ElementContent, Root, RootContent } from "hast";

interface StreamingFadeOptions {
  hasFinished?: boolean;
}

/**
 * Prose containers whose text fades in. Everything else — code, and the annotation and tool-call tags,
 * which have their own entrances and some of which read their children as a string — is left alone.
 */
const PROSE_TAGS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "strong",
  "em",
  "del",
  "a",
  "blockquote",
  "table",
  "thead",
  "tbody",
  "tr",
  "td",
  "th",
  "think",
]);

/** A word with the whitespace after it, or a whitespace run the text starts with. */
const WORD = /\S+\s*|\s+/g;

const tokenSpan = (value: string): Element => ({
  type: "element",
  tagName: "span",
  properties: { className: ["stream-token"] },
  children: [{ type: "text", value }],
});

function wrapWords(children: RootContent[]): RootContent[] {
  return children.flatMap((child): RootContent[] => {
    if (child.type === "text") {
      // Whitespace between blocks stays a plain text node.
      if (!child.value.trim()) return [child];
      return (child.value.match(WORD) ?? []).map(tokenSpan);
    }
    if (child.type === "element" && PROSE_TAGS.has(child.tagName)) {
      child.children = wrapWords(child.children) as ElementContent[];
    }
    return [child];
  });
}

/**
 * While a response streams, wraps each word of prose in a `stream-token` span that fades in as it mounts.
 *
 * React-markdown keys children by tag and position, so words already on screen keep their elements as
 * the text grows and only the new ones animate. Once the run is over nothing is wrapped: the finished
 * text is plain again and does not carry a span per word.
 */
export const rehypeStreamingFade = ({ hasFinished = false }: StreamingFadeOptions = {}) => {
  return (tree: Root) => {
    if (hasFinished) return;
    tree.children = wrapWords(tree.children);
  };
};
